import type React from 'react';
import { SituacaoChegadaPedido, StatusPrevisao } from '../../interfaces/PrevisaoPecas';

/**
 * Previsão e chegada são DIA PURO (gravados como meia-noite UTC): `new Date(...)` no fuso do
 * navegador os jogaria para o dia anterior. Por isso a data é recortada do texto, sem conversão.
 * Instante (hora em que alguém registrou) é outra coisa, e vai no fuso local.
 *
 * (Migrado de `views/estoque/formatos.ts`, removida na previsao-pecas/at-02.)
 */
export function formatarDiaPuro(iso: string | null | undefined, vazio = 'sem previsão'): string {
  if (!iso) return vazio;
  const [ano, mes, dia] = iso.slice(0, 10).split('-');
  return `${dia}/${mes}/${ano}`;
}

export function formatarInstante(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatarQuantidade(valor: number): string {
  return valor.toLocaleString('pt-BR', { maximumFractionDigits: 4 });
}

/** Hoje no fuso da oficina, `AAAA-MM-DD` — o teto do campo de data da chegada. */
export function hojeIso(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/**
 * Selo de status da peça, com a paleta compartilhada (`shared/ui/badge-status.css`).
 * ⚠️ "Atrasado" em laranja, nunca vermelho: a previsão passou sem chegada registrada, e a peça pode
 * ter chegado sem nota (inclusive direto numa oficina externa).
 */
export const STATUS_DA_PREVISAO: Record<StatusPrevisao, { rotulo: string; classe: string }> = {
  RECEBIDO: { rotulo: 'Recebido', classe: 'badge-verde' },
  ATRASADO: { rotulo: 'Atrasado', classe: 'badge-laranja' },
  CHEGA_HOJE: { rotulo: 'Chega hoje', classe: 'badge-azul' },
  NO_PRAZO: { rotulo: 'No prazo', classe: 'badge-cinza' },
};

/** O rótulo do status da peça. */
export function rotuloDoStatus(status: StatusPrevisao): string {
  return STATUS_DA_PREVISAO[status].rotulo;
}

/**
 * A situação de chegada do PEDIDO, pelo saldo dos itens. ⚠️ Não é a situação do ERP: um pedido
 * liquidado no Nasajon pode estar "Parcial" aqui, quando a nota não trouxe todos os itens.
 */
export const SITUACAO_DO_PEDIDO: Record<SituacaoChegadaPedido, { rotulo: string; classe: string }> = {
  SEM_CHEGADA: { rotulo: 'Sem chegada', classe: 'badge-cinza' },
  PARCIAL: { rotulo: 'Parcial', classe: 'badge-azul' },
  COMPLETO: { rotulo: 'Completo', classe: 'badge-verde' },
  CANCELADO: { rotulo: 'Cancelado', classe: 'badge-vermelho' },
};

/** A situação gerencial do ERP, por extenso (at-128). */
export const SITUACAO_GERENCIAL: Record<string, string> = {
  aberto: 'Aberto',
  aguardando_documento: 'Aguardando documento',
  parcialmente_liquidado: 'Parcialmente liquidado',
  liquidado: 'Liquidado',
  cancelado: 'Cancelado',
};

/** Célula de UMA linha (regra de 12/08/2026): o texto termina em reticências dentro da coluna. */
export const CELULA_DE_UMA_LINHA: React.CSSProperties = {
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
